import type { DiffTable, OrgDiff } from "../../../interfaces/ImportDiff.interface";
import { importFileExtension } from "./importFileTypes";

/**
 * The file types POST /import/organization/diff compares: the setup wizard's
 * FlatBuffer exports. Every other import format is refused by the endpoint,
 * because none carries the ids and columns the comparison matches on.
 */
export const DIFF_FILE_EXTENSIONS: readonly string[] = [".lnx", ".bin"];

/** True when name is a file the diff endpoint compares. */
export function isDiffFile(name: string): boolean {
  return DIFF_FILE_EXTENSIONS.includes(importFileExtension(name));
}

/** True when a table has anything other than unchanged entities. */
export function tableHasChanges(table: DiffTable): boolean {
  return table.new.length > 0 || table.modified.length > 0 || table.only_in_org.length > 0;
}

/**
 * Posts file to POST /import/organization/diff to compare it with orgId.
 * Read-only on the server; throws with the server's message on failure.
 */
export async function postImportDiff(
  baseUrl: string | undefined,
  token: string,
  orgId: string,
  file: Blob,
): Promise<OrgDiff> {
  const formData = new FormData();
  formData.append("org_id", orgId);
  formData.append("file", file);

  const response = await fetch(`${baseUrl}/import/organization/diff`, {
    method: "POST",
    headers: { authorization: token },
    body: formData,
  });

  const body = await response.json().catch(() => null);

  if (!response.ok) {
    const detail = body?.message ?? `status ${response.status}`;
    throw new Error(`Comparison failed: ${detail}`);
  }

  return body.data as OrgDiff;
}
